import i18n from '../i18n/client'

export enum NumberFormats {
	/**
	 * Standart formated value
	 * @example 1 -> 1
	 * @example 1.1 -> 1.1
	 * @example 1.11 -> 1.11
	 * @example 1000.1 -> 1,000.1
	 * @example 1000.11 -> 1,000.11
	 *
	 */
	Standart = 'standart',

	/**
	 * Compact formated value
	 * @example 1000 -> 1K
	 * @example 1000.25 -> 1.25k
	 * @example 1000000 -> 1M
	 * @example 1000000000 -> 1B
	 */
	Compact = 'compact',

	/**
	 * Integer formated value
	 * @example 1 -> 1
	 * @example 1.1 -> 1
	 * @example 1.11 -> 1
	 * @example 1000.1 -> 1,000
	 * @example 1000.11 -> 1,000
	 */
	Integer = 'integer',

	/**
	 * Fixed formated value with 2 digits after dot
	 * @example 1000 -> 1,000.00
	 * @example 1000.1 -> 1,000.10
	 * @example 1000.11 -> 1,000.11
	 * @example 1000.111 -> 1,000.11
	 */
	Fixed = 'fixed',

	/**
	 * Percent formated value
	 * @example 1 -> 1.0%
	 * @example 1.1 -> 1.1%
	 * @example 1.11 -> 1.1%
	 * @example 1000.1 -> 1,000.1%
	 * @example 1000.11 -> 1,000.1%
	 * @example 1000.111 -> 1,000.1%
	 */
	Percent = 'percent',

	/**
	 * Percent formated value with sign
	 * @example 1 -> +1.0%
	 * @example 1.1 -> +1.1%
	 * @example 1.11 -> +1.1%
	 * @example -1 -> -1.0%
	 * @example -1.1 -> -1.1%
	 * @example -1.11 -> -1.1%
	 */
	PercentSigned = 'percentSigned',

	/**
	 * Requires currency formatted value
	 * @example 1000 -> 1,000.00$
	 * @example 1000.1 -> 1,000.10$
	 * @example 1000.11 -> 1,000.11$
	 */
	Currency = 'currency',
}
export type NumbersConfig = {
	locales: string[]
	getLocale: () => string
	currencyCode?: string
}
export type NumbersApi = {
	formatNumber(value: number, frmt: NumberFormats): string
	config: NumbersConfig
}
const createLocaleFormatters = (locale: string, currencyCode: string) =>
	new Map<NumberFormats, Intl.NumberFormat>([
		[
			NumberFormats.Standart,
			new Intl.NumberFormat(locale, {
				maximumFractionDigits: 2,
			}),
		],
		[
			NumberFormats.Compact,
			new Intl.NumberFormat(locale, {
				notation: 'compact',
				maximumFractionDigits: 2,
			}),
		],
		[
			NumberFormats.Integer,
			new Intl.NumberFormat(locale, {
				maximumFractionDigits: 0,
			}),
		],
		[
			NumberFormats.Fixed,
			new Intl.NumberFormat(locale, {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2,
			}),
		],
		[
			NumberFormats.Percent,
			new Intl.NumberFormat(locale, {
				style: 'percent',
				maximumFractionDigits: 1,
				minimumFractionDigits: 0,
			}),
		],
		[
			NumberFormats.PercentSigned,
			new Intl.NumberFormat(locale, {
				signDisplay: 'exceptZero',
				style: 'percent',
				maximumFractionDigits: 1,
				minimumFractionDigits: 1,
			}),
		],
		[
			NumberFormats.Currency,
			new Intl.NumberFormat(locale, {
				style: 'currency',
				currency: currencyCode,
				maximumFractionDigits: 2,
				minimumFractionDigits: 2,
			}),
		],
	])

function initNumbers(config: NumbersConfig): NumbersApi {
	const defaultLocale = createLocaleFormatters('en', 'USD')
	const locales = new Map<string, Map<NumberFormats, Intl.NumberFormat>>(
		config.locales.map((locale) => [
			locale,
			createLocaleFormatters(locale, 'USD'),
		])
	)

	function formatNumber(
		value: number,
		frmt: NumberFormats = NumberFormats.Standart
	) {
		if (
			frmt === NumberFormats.Percent ||
			frmt === NumberFormats.PercentSigned
		) {
			value = value / 100
		}
		const formatters = locales.get(config.getLocale()) || defaultLocale
		if (formatters.has(frmt)) return formatters.get(frmt)?.format(value) || ''
		else return defaultLocale.get(frmt)?.format(value) || ''
	}

	return {
		formatNumber,
		config,
	}
}

const numbers = initNumbers({
	locales: ['en', 'uk', 'es', 'de'],
	getLocale: () => i18n.language || 'en-US',
	currencyCode: 'USD',
})
export const formatNumber = (
	value: number,
	format: NumberFormats = NumberFormats.Standart
) => numbers.formatNumber(value, format)

const moneyOptions: Partial<Record<NumberFormats, Intl.NumberFormatOptions>> = {
	[NumberFormats.Compact]: { notation: 'compact', maximumFractionDigits: 2 },
	[NumberFormats.Integer]: { maximumFractionDigits: 0 },
	[NumberFormats.Fixed]: {
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
	},
}

/**
 * Money with the currency sign placed per locale.
 * @example formatMoney(1500, 'USD') -> $1.5K
 */
export const formatMoney = (
	value: number,
	currency?: string | null,
	format: NumberFormats = NumberFormats.Compact
) => {
	if (!currency) return formatNumber(value, format)
	try {
		return new Intl.NumberFormat(i18n.language || 'en-US', {
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
			...moneyOptions[format],
			style: 'currency',
			currency,
			currencyDisplay: 'narrowSymbol',
		}).format(value)
	} catch {
		return `${currency} ${formatNumber(value, format)}`
	}
}

export const getCurrencySign = (code: string, locale?: string) => {
	try {
		return (
			new Intl.NumberFormat(locale || i18n.language || 'en-US', {
				style: 'currency',
				currency: code,
				currencyDisplay: 'narrowSymbol',
			})
				.formatToParts(0)
				.find((part) => part.type === 'currency')?.value ?? code
		)
	} catch {
		return code
	}
}
